// Frontend Database Utilities for Admin Panel
// Bu dosyada backend API'lerine çağrılar yapan fonksiyonlar bulunur

import { authenticatedFetch } from '../services/auth-frontend.js';

const API_BASE_URL ='https://api.onurbeton.com.tr/api';

// Generic fetch function with error handling (for public GET requests)
const fetchWithErrorHandling = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

// Products API
export const productsAPI = {
  // Get all products (Public)
  getAll: async (includeInactive = false) => {
    const params = includeInactive ? '?includeInactive=true' : '';
    return await fetchWithErrorHandling(`${API_BASE_URL}/products${params}`);
  },

  // Get product by ID (Public)
  getById: async (id) => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/products/${id}`);
  },

  // Create new product (Admin only)
  create: async (productData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      body: JSON.stringify(productData),
    });
    return await response.json();
  },

  // Update product (Admin only)
  update: async (id, productData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
    return await response.json();
  },

  // Delete product (Admin only)
  delete: async (id) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
    });
    return await response.json();
  }
};

// Services API
export const servicesAPI = {
  // Get all services (Public)
  getAll: async (includeInactive = false) => {
    const params = includeInactive ? '?includeInactive=true' : '';
    return await fetchWithErrorHandling(`${API_BASE_URL}/services${params}`);
  },

  // Get service by ID (Public)
  getById: async (id) => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/services/${id}`);
  },

  // Create new service (Admin only)
  create: async (serviceData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/services`, {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
    return await response.json();
  },

  // Update service (Admin only)
  update: async (id, serviceData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
    return await response.json();
  },

  // Delete service (Admin only)
  delete: async (id) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/services/${id}`, {
      method: 'DELETE',
    });
    return await response.json();
  }
};

// References API
export const referencesAPI = {
  // Get all references (Public)
  getAll: async (includeInactive = false) => {
    const params = includeInactive ? '?includeInactive=true' : '';
    return await fetchWithErrorHandling(`${API_BASE_URL}/references${params}`);
  },

  // Get reference by ID (Public)
  getById: async (id) => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/references/${id}`);
  },

  // Create new reference (Admin only)
  create: async (referenceData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/references`, {
      method: 'POST',
      body: JSON.stringify(referenceData),
    });
    return await response.json();
  },

  // Update reference (Admin only)
  update: async (id, referenceData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/references/${id}`, {
      method: 'PUT',
      body: JSON.stringify(referenceData),
    });
    return await response.json();
  },

  // Delete reference (Admin only)
  delete: async (id) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/references/${id}`, {
      method: 'DELETE',
    });
    return await response.json();
  }
};

// News API
export const newsAPI = {
  // Get all news (Public)
  getAll: async (includeUnpublished = false) => {
    const params = includeUnpublished ? '?includeUnpublished=true' : '';
    return await fetchWithErrorHandling(`${API_BASE_URL}/news${params}`);
  },

  // Get news by ID (Public)
  getById: async (id) => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/news/${id}`);
  },

  // Create new news (Admin only)
  create: async (newsData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/news`, {
      method: 'POST',
      body: JSON.stringify(newsData),
    });
    return await response.json();
  },

  // Update news (Admin only)
  update: async (id, newsData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(newsData),
    });
    return await response.json();
  },

  // Delete news (Admin only)
  delete: async (id) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/news/${id}`, {
      method: 'DELETE',
    });
    return await response.json();
  }
};

// Gallery API
export const galleryAPI = {
  // Get all gallery images (Public)
  getAll: async () => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/gallery`);
  },

  // Get gallery image by ID (Public)
  getById: async (id) => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/gallery/${id}`);
  },

  // Get gallery by category (Public)
  getByCategory: async (category) => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/gallery/category/${category}`);
  },

  // Get all categories (Public)
  getCategories: async () => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/gallery/categories`);
  },

  // Create new gallery image (Admin only)
  create: async (galleryData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/gallery`, {
      method: 'POST',
      body: JSON.stringify(galleryData),
    });
    return await response.json();
  },

  // Update gallery image (Admin only)
  update: async (id, galleryData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/gallery/${id}`, {
      method: 'PUT',
      body: JSON.stringify(galleryData),
    });
    return await response.json();
  },

  // Delete gallery image (Admin only)
  delete: async (id) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/gallery/${id}`, {
      method: 'DELETE',
    });
    return await response.json();
  }
};

// Contacts API
export const contactsAPI = {
  // Get all contacts (Admin only)
  getAll: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/contacts`);
    return await response.json();
  },

  // Create contact (Public - contact form)
  create: async (contactData) => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/contacts`, {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
  },

  // Mark contact as read (Admin only)
  markAsRead: async (id) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/contacts/${id}/read`, {
      method: 'PUT',
    });
    return await response.json();
  }
};

// Admin helpers
export const adminHelpers = {
  // Get dashboard overview (Admin only)
  getDashboardStats: async () => {
    try {
      const [products, services, references, news, gallery] = await Promise.all([
        productsAPI.getAll(true), // Include inactive
        servicesAPI.getAll(true), // Include inactive
        referencesAPI.getAll(true), // Include inactive
        newsAPI.getAll(true), // Include unpublished
        galleryAPI.getAll()
      ]);

      return {
        products: {
          total: products.length,
          active: products.filter(p => p.is_active !== false).length
        },
        services: {
          total: services.length,
          active: services.filter(s => s.is_active !== false).length
        },
        references: {
          total: references.length,
          active: references.filter(r => r.is_active !== false).length
        },
        news: {
          total: news.length,
          published: news.filter(n => n.is_published === true).length
        },
        gallery: {
          total: gallery.length,
          featured: gallery.filter(g => g.is_featured === true).length
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }
};

// Documents API
export const documentsAPI = {
  // Get all documents (Public)
  getAll: async (includeInactive = false) => {
    const params = includeInactive ? '?includeInactive=true' : '';
    return await fetchWithErrorHandling(`${API_BASE_URL}/documents${params}`);
  },

  // Get document by ID (Public)
  getById: async (id) => {
    return await fetchWithErrorHandling(`${API_BASE_URL}/documents/${id}`);
  },

  // Create new document (Admin only)
  create: async (documentData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/documents`, {
      method: 'POST',
      body: JSON.stringify(documentData),
    });
    return await response.json();
  },

  // Update document (Admin only)
  update: async (id, documentData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(documentData),
    });
    return await response.json();
  },

  // Delete document (Admin only)
  delete: async (id) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/documents/${id}`, {
      method: 'DELETE',
    });
    return await response.json();
  },

  // Toggle active status (Admin only)
  toggleActive: async (id) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/documents/${id}/toggle-active`, {
      method: 'PATCH',
    });
    return await response.json();
  }
};

// Export main API object for convenience
export const API = {
  products: productsAPI,
  services: servicesAPI,
  references: referencesAPI,
  news: newsAPI,
  gallery: galleryAPI,
  contacts: contactsAPI,
  documents: documentsAPI,
  admin: adminHelpers
}; 